import React, {
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import {isMobile as isMobileFunc} from "../../../utils/MobileUtils";
import {get, isEqual} from "lodash";
import {filterOptions} from "./utils";
import Input from "../input/Input";
import ReactDOM from "react-dom";
import Tooltip from "../tooltip/Tooltip";
import OptionListDesktop from "./OptionListDesktop";
import Checkbox from "../checkbox/Checkbox";
import {catchify} from "../../../utils/Utils";
import "./_select.scss";

// tekst opcji - tak samo jak w SelectItem i placeholderze Select
const getOptionLabel = (option) =>
    get(
        typeof option.value === "object" ? option.value : option,
        option.name,
        option.name ?? option.value ?? ""
    );

/**
 * Wybór wielu wartości na bazie Select: ta sama lista opcji, filtrowanie wpisywaniem i nawigacja strzałkami.
 * Różnice: `value` to tablica, kliknięcie / Enter przełącza opcję bez zamykania listy, Backspace przy pustym
 * polu odznacza ostatnią wartość, a pole pokazuje wybrane wartości po przecinku (albo "N wybranych").
 * Na mobile - natywny `<select multiple>`.
 * @param {*} param0
 * @returns
 */
const MultiSelect = ({
    disabled,
    mobile,
    onChange,
    options: _options,
    value,
    id,
    placeholder,
    clearButton,
    renderInPortal,
    error,
    warning,
    showIconOnErrorOnWarning,
    autocomplete,
    maxLabels,
    selectedCountLabel,
    onBlur,
}) => {
    const [inputBBox, setInputBBox] = useState({});

    const selectContainerRef = useRef();
    const selectRef = useRef();
    const caretRef = useRef();

    const [filterText, setFilterText] = useState("");
    const [hovered, setHovered] = useState(null);
    const [isOpened, setOpened] = useState(false);

    const selected = useMemo(() => value ?? [], [value]);

    const isMobile = useMemo(() => {
        if ([false, true].includes(mobile)) return mobile;
        return isMobileFunc();
    }, [mobile]);

    const getInputNode = useCallback(() => {
        if (selectRef.current) {
            return selectRef.current
                .getElementsByClassName("fetura-input")[0]
                .getElementsByTagName("input")[0];
        }
        return null;
    }, []);

    const focusInput = useCallback(() => {
        catchify(() => {
            const input = getInputNode();
            if (input) input.focus();
        })();
    }, [getInputNode]);

    const isActive = useCallback(
        (option) => selected.some((item) => isEqual(item, option.value)),
        [selected]
    );

    // opcja z checkboxem aplikacji (lista wspólna z Select); readOnly - kliknięcie obsługuje wiersz listy,
    // inaczej etykieta checkboxa przełączałaby opcję drugi raz
    const optionRenderer = useCallback(
        (option) => (
            <Checkbox
                className="multi-select-option"
                label={String(getOptionLabel(option))}
                checked={isActive(option)}
                margin={false}
                readOnly
            />
        ),
        [isActive]
    );

    const options = useMemo(
        () => filterOptions(filterText, _options),
        [filterText, _options]
    );

    const handleCaretClick = useCallback(
        (e) => {
            if (isOpened) {
                setOpened(false);
                e.preventDefault();
                e.stopPropagation();
            } else {
                focusInput();
            }
        },
        [isOpened, focusInput]
    );

    const handleInputFocus = useCallback(() => {
        setOpened(true);
    }, []);

    const handleSearchChange = useCallback((text) => {
        setFilterText(text ?? "");
    }, []);

    // przełączenie opcji - lista zostaje otwarta, fokus wraca do pola (zamknięcie kliknięciem obok działa dalej)
    const handleToggle = useCallback(
        (option) => {
            if (!option) return;
            const next = isActive(option)
                ? selected.filter((item) => !isEqual(item, option.value))
                : [...selected, option.value];
            onChange(next);
            focusInput();
        },
        [isActive, selected, onChange, focusInput]
    );

    const getContainer = useCallback(() => {
        const defaultValue =
            document.getElementById("root") ?? document.createElement("div");
        if (!selectRef.current) return defaultValue;
        // menu w modalu, żeby kliknięcie opcji nie zamykało modala
        const findModal = (element) => {
            if (element.classList.contains("modal")) return element;
            return element?.parentElement
                ? findModal(element.parentElement)
                : defaultValue;
        };
        return findModal(selectRef.current);
    }, []);

    const updateInputBBox = useCallback(() => {
        const styles = {};
        if (selectRef.current) {
            styles.width = selectRef.current.clientWidth;
            styles.height = selectRef.current.clientHeight;
            const inputRect = selectRef.current.getBoundingClientRect();
            const selectOptionsHeight =
                selectContainerRef?.current?.clientHeight ?? 0;
            const container = getContainer();
            const scrollY = container?.classList?.contains("modal")
                ? container.scrollTop
                : window.scrollY;
            styles.left = inputRect.left;
            if (
                inputRect.top + inputRect.height + selectOptionsHeight <
                document.documentElement.clientHeight
            ) {
                styles.top = inputRect.top + inputRect.height + scrollY;
            } else {
                styles.top = inputRect.top + scrollY - selectOptionsHeight;
            }
        }
        if (!isEqual(styles, inputBBox)) {
            setInputBBox(styles);
        }
    }, [inputBBox, getContainer]);

    useEffect(() => {
        if (!isOpened || isMobile) return undefined;
        const container = getContainer();
        container.addEventListener("scroll", updateInputBBox);
        return () => container.removeEventListener("scroll", updateInputBBox);
    }, [isOpened, getContainer, isMobile, updateInputBBox]);

    useLayoutEffect(() => {
        updateInputBBox();
    }, [updateInputBBox, isOpened]);

    // pole pokazuje wybrane wartości; przy dłuższej liście - "N wybranych"
    const summary = useMemo(() => {
        const labels = _options.filter(isActive).map(getOptionLabel);
        if (labels.length === 0) return placeholder || "";
        if (labels.length > maxLabels) {
            return selectedCountLabel
                ? selectedCountLabel(labels.length)
                : `${labels.length}`;
        }
        return labels.join(", ");
    }, [_options, isActive, placeholder, maxLabels, selectedCountLabel]);

    const handleInputBlur = useCallback(
        (e) => {
            if (
                selectContainerRef.current &&
                e.relatedTarget !== caretRef.current &&
                !selectContainerRef.current.contains(e.relatedTarget)
            ) {
                setOpened(false);
            }
            onBlur(undefined, e);
        },
        [onBlur]
    );

    const handleClearClick = useCallback(() => {
        setOpened(false);
        onChange([]);
    }, [onChange]);

    // strzałki - nawigacja, Enter - przełącza podświetloną opcję, Backspace (puste pole) - odznacza ostatnią,
    // Esc - zamyka listę
    const handleKeyDown = useCallback(
        (e) => {
            if (e.which === 8 && !filterText && selected.length > 0) {
                onChange(selected.slice(0, -1));
                return false;
            }
            if (!isOpened) return false;
            switch (e.which) {
                case 27: {
                    e.stopPropagation();
                    setOpened(false);
                    break;
                }
                case 38:
                case 40: {
                    if (!options.length) break;
                    const moveX = e.which - 39;
                    let nextHovered = hovered ?? (moveX > 0 ? -1 : 0);
                    nextHovered += moveX;
                    nextHovered = Math.max(
                        0,
                        Math.min(options.length - 1, nextHovered)
                    );
                    setHovered(nextHovered);
                    const item =
                        selectContainerRef?.current?.children?.[nextHovered];
                    if (item) item.scrollIntoView({block: "nearest"});
                    break;
                }
                case 13: {
                    e.stopPropagation();
                    e.preventDefault();
                    handleToggle(
                        options[hovered] ?? (filterText ? options[0] : null)
                    );
                    break;
                }
                default:
                    break;
            }
            return false;
        },
        [
            filterText,
            selected,
            isOpened,
            options,
            hovered,
            onChange,
            handleToggle,
        ]
    );

    const handleMobileChange = useCallback(
        (e) => {
            const next = Array.from(e.target.selectedOptions)
                .map((option) => _options[+option.value]?.value)
                .filter((item) => item !== undefined);
            onChange(next);
        },
        [_options, onChange]
    );

    const mobileValue = useMemo(
        () =>
            _options
                .map((option, index) =>
                    isActive(option) ? String(index) : null
                )
                .filter(Boolean),
        [_options, isActive]
    );

    const optionList = (
        <OptionListDesktop
            ref={selectContainerRef}
            inputBBox={inputBBox}
            value={selected}
            isActive={isActive}
            options={options}
            customRenderer={optionRenderer}
            hovered={hovered}
            menuOpened={isOpened}
            renderInPortal={renderInPortal}
            setFilterText={setFilterText}
            onHoverChange={setHovered}
            onChange={handleToggle}
        />
    );

    return (
        <div
            className={`fetura-select multi-select${isMobile ? " mobile" : ""}`}
            ref={selectRef}>
            <Input
                id={id}
                type="search"
                value={filterText}
                onChange={handleSearchChange}
                disabled={disabled}
                placeholder={summary}
                readOnly={isMobile}
                error={error}
                warning={warning}
                onClick={isMobile || disabled ? null : handleInputFocus}
                showIconOnErrorOnWarning={showIconOnErrorOnWarning}
                onFocus={isMobile || disabled ? null : handleInputFocus}
                onBlur={handleInputBlur}
                onKeyDown={handleKeyDown}
                autocomplete={autocomplete}
            />
            <div className="fetura-select-icons">
                <i
                    className={
                        isOpened
                            ? "fas fa-caret-up pointer"
                            : "fas fa-caret-down pointer"
                    }
                    onClick={isMobile ? null : handleCaretClick}
                    tabIndex={-1}
                    ref={caretRef}
                />
            </div>
            {isMobile && (
                <select
                    multiple
                    value={mobileValue}
                    disabled={disabled}
                    onChange={handleMobileChange}>
                    {_options.map((option, index) => (
                        <option key={index} value={String(index)}>
                            {getOptionLabel(option)}
                        </option>
                    ))}
                </select>
            )}
            {!isMobile &&
                (renderInPortal
                    ? ReactDOM.createPortal(optionList, getContainer())
                    : optionList)}
            {!!clearButton && !disabled && selected.length > 0 && (
                <div className="fetura-select-icons clear-button ">
                    <i
                        className="fas fa-times pointer"
                        onClick={handleClearClick}
                        tabIndex={-1}
                    />
                </div>
            )}
            {showIconOnErrorOnWarning && (!!error || !!warning) && (
                <Tooltip
                    tooltipContent={error || warning}
                    type={error ? "error" : "warning"}>
                    <div
                        className={`input-icon ${error ? "error" : "warning"}`}
                    />
                </Tooltip>
            )}
        </div>
    );
};

MultiSelect.defaultProps = {
    onChange: () => {},
    value: [],
    placeholder: "",
    clearButton: true,
    onBlur: () => {},
    renderInPortal: true,
    mobile: null,
    autocomplete: "off",
    // powyżej tej liczby pole pokazuje "N wybranych" zamiast wartości
    maxLabels: 2,
};

export default MultiSelect;
